import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ChevronDown, ChevronUp, X, Check, Search } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  buildBrandFilterEntries,
  getBrandLogo,
  type BrandFilterEntry,
} from '@/lib/brandLogos';

interface VehicleFiltersProps {
  // Search
  searchTerm: string;
  setSearchTerm: (value: string) => void;

  // Filter states
  selectedBrand: string;
  setSelectedBrand: (value: string) => void;
  selectedModel: string;
  setSelectedModel: (value: string) => void;
  selectedFuels: string[];
  setSelectedFuels: (value: string[]) => void;
  selectedTransmissions: string[];
  setSelectedTransmissions: (value: string[]) => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
  selectedColors: string[];
  setSelectedColors: (value: string[]) => void;
  selectedOptionals: string[];
  setSelectedOptionals: (value: string[]) => void;
  selectedStores: string[];
  setSelectedStores: (value: string[]) => void;
  isArmoredFilter?: boolean;
  setIsArmoredFilter?: (value: boolean) => void;
  priceRange: number[];
  setPriceRange: (value: number[]) => void;
  yearRange: number[];
  setYearRange: (value: number[]) => void;
  mileageRange: number[];
  setMileageRange: (value: number[]) => void;

  // UI Control
  hideHeader?: boolean;
  hideSearch?: boolean;
  hideClearAll?: boolean;
  hiddenSections?: string[];
  layout?: 'sidebar' | 'drawer';

  // Data
  config: any; // Config object to check unifiedAds
  brands: string[];
  models: string[];
  fuels: string[];
  transmissions: string[];
  categories: any[];
  colors: string[];
  optionals: string[];
  availableStores: any[]; // Available stores for filtering
  selectedStore: any; // Currently selected store
  vehicles: any[]; // All vehicles (for calculating counts)
  ranges: {
    minPrice: number;
    maxPrice: number;
    minYear: number;
    maxYear: number;
    minMileage: number;
    maxMileage: number;
  };

  // Actions
  clearFilters: () => void;
  hasActiveFilters: boolean;
  filteredCount: number;

  // Expandable sections
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
}

export const VehicleFilters = ({
  searchTerm,
  setSearchTerm,
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  selectedFuels,
  setSelectedFuels,
  selectedTransmissions,
  setSelectedTransmissions,
  selectedType,
  setSelectedType,
  selectedCategory,
  setSelectedCategory,
  selectedColors,
  setSelectedColors,
  selectedOptionals,
  setSelectedOptionals,
  selectedStores,
  setSelectedStores,
  isArmoredFilter = false,
  setIsArmoredFilter,
  priceRange,
  setPriceRange,
  yearRange,
  setYearRange,
  mileageRange,
  setMileageRange,
  config,
  brands,
  models,
  fuels,
  transmissions,
  categories,
  colors,
  optionals,
  availableStores,
  selectedStore,
  vehicles,
  ranges,
  clearFilters,
  hasActiveFilters,
  filteredCount,
  expandedSections,
  toggleSection,
  hideHeader = false,
  hideSearch = false,
  hideClearAll = false,
  hiddenSections = [],
  layout = 'sidebar',
}: VehicleFiltersProps) => {
  const isDrawerLayout = layout === 'drawer';
  const { colors: themeColors } = useTheme();
  
  // Check if unified ads is enabled (default to true)
  const unifiedAds = config?.unifiedAds !== false;
  const unifiedAdsStore = config?.unifiedAdsStore; // Can be null or store ID
  const selectedStoreId = selectedStore?._id;
  
  // Determine if store filter should be shown:
  // - Show if: unifiedAds is true AND (unifiedAdsStore is null OR selected store is unifiedAdsStore)
  // - Hide if: unifiedAds is false OR (unifiedAds is true AND unifiedAdsStore is set AND selected store is not unifiedAdsStore)
  const showStoreFilter = unifiedAds && (
    unifiedAdsStore === null || 
    unifiedAdsStore === undefined || 
    selectedStoreId === unifiedAdsStore
  );
  
  const [showAllBrands, setShowAllBrands] = useState(false);

  const brandEntries = useMemo(
    () => buildBrandFilterEntries(vehicles, selectedCategory),
    [vehicles, selectedCategory]
  );

  const isBrandEntrySelected = (entry: BrandFilterEntry) => {
    if (selectedBrand !== entry.brand) return false;
    if (entry.bindsCategory) {
      return selectedCategory === String(entry.vehicleType);
    }
    if (selectedCategory !== 'all') {
      return selectedCategory === String(entry.vehicleType);
    }
    return true;
  };

  const toggleBrandEntry = (entry: BrandFilterEntry) => {
    if (isBrandEntrySelected(entry)) {
      setSelectedBrand('all');
      if (entry.bindsCategory) {
        setSelectedCategory('all');
      }
      return;
    }

    setSelectedBrand(entry.brand);
    if (entry.bindsCategory) {
      setSelectedCategory(String(entry.vehicleType));
    }
  };

  // Reset model when brand changes
  useEffect(() => {
    // Remove window.scrollTo to keep filter scroll position
    if (selectedModel !== 'all') {
      setTimeout(() => {
        setSelectedModel('all');
      }, 100);
    }
  }, [selectedBrand]);

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `R$ ${(price / 1000000).toFixed(1)}M`;
    }
    if (price >= 1000) {
      return `R$ ${(price / 1000).toFixed(0)}k`;
    }
    return `R$ ${price}`;
  };

  const formatPriceLabel = (price: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(price);

  const selectedStyle = (isSelected: boolean) =>
    isSelected
      ? {
          borderColor: themeColors.primary,
          backgroundColor: `${themeColors.primary}12`,
        }
      : undefined;

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      chips.push({
        key: 'search',
        label: `Busca: ${trimmedSearch}`,
        onRemove: () => setSearchTerm(''),
      });
    }

    if (selectedCategory !== 'all') {
      const category = categories.find((c: { value: string; label: string }) => c.value === selectedCategory);
      chips.push({
        key: `category-${selectedCategory}`,
        label: category?.label || selectedCategory,
        onRemove: () => setSelectedCategory('all'),
      });
    }

    if (selectedType !== 'all') {
      chips.push({
        key: `type-${selectedType}`,
        label: selectedType === 'zerokm' ? 'Novos' : 'Usados',
        onRemove: () => setSelectedType('all'),
      });
    }

    if (selectedBrand !== 'all') {
      chips.push({
        key: `brand-${selectedBrand}`,
        label: selectedBrand,
        onRemove: () => {
          setSelectedBrand('all');
          setSelectedModel('all');
        },
      });
    }

    if (selectedModel !== 'all') {
      chips.push({
        key: `model-${selectedModel}`,
        label: selectedModel,
        onRemove: () => setSelectedModel('all'),
      });
    }

    selectedFuels.forEach((fuel) => {
      chips.push({
        key: `fuel-${fuel}`,
        label: fuel,
        onRemove: () => setSelectedFuels(selectedFuels.filter((f) => f !== fuel)),
      });
    });

    selectedTransmissions.forEach((transmission) => {
      chips.push({
        key: `transmission-${transmission}`,
        label: transmission,
        onRemove: () =>
          setSelectedTransmissions(selectedTransmissions.filter((t) => t !== transmission)),
      });
    });

    selectedColors.forEach((color) => {
      chips.push({
        key: `color-${color}`,
        label: color,
        onRemove: () => setSelectedColors(selectedColors.filter((c) => c !== color)),
      });
    });

    selectedOptionals.forEach((optional) => {
      chips.push({
        key: `optional-${optional}`,
        label: optional,
        onRemove: () => setSelectedOptionals(selectedOptionals.filter((o) => o !== optional)),
      });
    });

    selectedStores.forEach((storeId) => {
      const store = availableStores.find((s) => s._id === storeId);
      chips.push({
        key: `store-${storeId}`,
        label: store?.company || 'Loja',
        onRemove: () => setSelectedStores(selectedStores.filter((id) => id !== storeId)),
      });
    });

    if (isArmoredFilter && setIsArmoredFilter) {
      chips.push({
        key: 'armored',
        label: 'Blindados',
        onRemove: () => setIsArmoredFilter(false),
      });
    }

    if (priceRange[0] > ranges.minPrice || priceRange[1] < ranges.maxPrice) {
      chips.push({
        key: 'price',
        label: `Preço: ${formatPriceLabel(priceRange[0])} – ${formatPriceLabel(priceRange[1])}`,
        onRemove: () => setPriceRange([ranges.minPrice, ranges.maxPrice]),
      });
    }

    if (yearRange[0] > ranges.minYear || yearRange[1] < ranges.maxYear) {
      chips.push({
        key: 'year',
        label: `Ano: ${yearRange[0]} – ${yearRange[1]}`,
        onRemove: () => setYearRange([ranges.minYear, ranges.maxYear]),
      });
    }

    if (mileageRange[0] > ranges.minMileage || mileageRange[1] < ranges.maxMileage) {
      chips.push({
        key: 'mileage',
        label: `Km: ${mileageRange[0].toLocaleString('pt-BR')} – ${mileageRange[1].toLocaleString('pt-BR')}`,
        onRemove: () => setMileageRange([ranges.minMileage, ranges.maxMileage]),
      });
    }

    return chips;
  }, [
    searchTerm,
    selectedCategory,
    selectedType,
    selectedBrand,
    selectedModel,
    selectedFuels,
    selectedTransmissions,
    selectedColors,
    selectedOptionals,
    selectedStores,
    isArmoredFilter,
    priceRange,
    yearRange,
    mileageRange,
    categories,
    availableStores,
    ranges,
    setSearchTerm,
    setSelectedCategory,
    setSelectedType,
    setSelectedBrand,
    setSelectedModel,
    setSelectedFuels,
    setSelectedTransmissions,
    setSelectedColors,
    setSelectedOptionals,
    setSelectedStores,
    setIsArmoredFilter,
    setPriceRange,
    setYearRange,
    setMileageRange,
  ]);

  // Calculate counts for each filter option based on all vehicles
  const getCategoryCount = (categoryValue: string) => {
    return vehicles.filter(v => v.type.toString() === categoryValue).length;
  };

  const getFuelCount = (fuel: string) => {
    return vehicles.filter(v => v.fuel === fuel).length;
  };

  const getTransmissionCount = (transmission: string) => {
    return vehicles.filter(v => v.transmission === transmission).length;
  };

  const getColorCount = (color: string) => {
    return vehicles.filter(v => v.color === color).length;
  };

  const getOptionalCount = (optional: string) => {
    return vehicles.filter(v => v.optionals?.includes(optional)).length;
  };

  const getStoreCount = (storeId: string) => {
    return vehicles.filter(v => v.store === storeId).length;
  };

  // Generate dynamic price options (10 values between min and max)
  const generatePriceOptions = () => {
    const min = ranges.minPrice;
    const max = ranges.maxPrice;
    const step = Math.ceil((max - min) / 9);
    const options = [min];

    for (let i = 1; i < 9; i++) {
      const value = Math.round((min + (step * i)) / 5000) * 5000; // Round to nearest 5k
      if (value < max && !options.includes(value)) {
        options.push(value);
      }
    }
    options.push(max);
    return options;
  };

  // Generate dynamic year options for minimum year (includes modelYear - 1 for each vehicle)
  const generateMinYearOptions = () => {
    // Get all unique modelYears from vehicles
    const modelYears = Array.from(new Set(vehicles.map(v => v.modelYear)));

    // For each modelYear, include both (modelYear - 1) and modelYear
    const allYears = new Set<number>();
    modelYears.forEach(year => {
      allYears.add(year - 1); // Previous year
      allYears.add(year);     // Current year
    });

    // Ensure current yearRange[0] is in the options (for URL params)
    if (yearRange[0]) {
      allYears.add(yearRange[0]);
    }

    // Convert to sorted array (descending)
    return Array.from(allYears).sort((a, b) => b - a);
  };

  // Generate dynamic year options for maximum year (only actual modelYears)
  const generateMaxYearOptions = () => {
    // Get all unique modelYears from vehicles
    const modelYears = Array.from(new Set(vehicles.map(v => v.modelYear)));

    // Ensure current yearRange[1] is in the options (for URL params)
    const allYears = new Set(modelYears);
    if (yearRange[1]) {
      allYears.add(yearRange[1]);
    }

    // Return sorted array (descending)
    return Array.from(allYears).sort((a, b) => b - a);
  };

  // Generate dynamic mileage options (10 values between min and max)
  const generateMileageOptions = () => {
    const min = ranges.minMileage;
    const max = ranges.maxMileage;
    const step = Math.ceil((max - min) / 9);
    const options = [min];

    for (let i = 1; i < 9; i++) {
      const value = Math.round((min + (step * i)) / 1000) * 1000; // Round to nearest 1k
      if (value < max && !options.includes(value)) {
        options.push(value);
      }
    }
    options.push(max);
    return options;
  };

  const isSectionHidden = (section: string) => hiddenSections.includes(section);

  const FilterSection = ({
    title,
    section,
    children,
    alwaysExpanded = false
  }: {
    title: string;
    section: string;
    children: React.ReactNode;
    alwaysExpanded?: boolean;
  }) => {
    if (isSectionHidden(section)) return null;

    return (
    <div className="py-5 border-border/50">
      {!alwaysExpanded ? (
        <>
          <button
            onClick={() => toggleSection(section)}
            className="w-full flex items-center justify-between mb-4 group"
          >
            <h3 className="font-bold text-base text-foreground">
              {title}
            </h3>
            {expandedSections[section] ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            )}
          </button>
          {expandedSections[section] && (
            <div className="space-y-3">
              {children}
            </div>
          )}
        </>
      ) : (
        <>
          {title && (
            <h3 className="font-bold text-base text-foreground mb-4">
              {title}
            </h3>
          )}
          <div className="space-y-3">
            {children}
          </div>
        </>
      )}
    </div>
    );
  };

  const useExternalScroll = isDrawerLayout && hideSearch;

  return (
    <div className={useExternalScroll ? 'space-y-0' : isDrawerLayout ? 'flex flex-col h-full min-h-0' : 'flex flex-col h-full max-h-[calc(100vh-180px)]'}>
      {/* Header with Clear Filters and Search - Fixed at top */}
      <div className="flex-shrink-0">
        {!hideHeader && (
          <>
            <div className="mb-3">
              <h2 className="text-lg font-bold text-foreground">Filtros</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {filteredCount} {filteredCount === 1 ? 'veículo' : 'veículos'}
              </p>
            </div>
          </>
        )}

        {/* Store Filter - Dropdown (Only show if multiple stores AND showStoreFilter is true) */}
        {availableStores.length > 1 && showStoreFilter && (
          <div className="mb-1">
            <Select
              value={selectedStores.length === 1 ? selectedStores[0] : 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  setSelectedStores([]);
                } else {
                  setSelectedStores([value]);
                }
              }}
            >
              <SelectTrigger className="w-full [&>span]:flex [&>span]:items-center [&>span]:justify-between [&>span]:w-full">
                <SelectValue placeholder="Todas as lojas">
                  {selectedStores.length === 1 ? (
                    <>
                      <span className="truncate">
                        {availableStores.find(s => s._id === selectedStores[0])?.company || 'Loja'}
                      </span>
                      <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-4 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground flex-shrink-0 mr-1">
                        {getStoreCount(selectedStores[0])}
                      </span>
                    </>
                  ) : (
                    'Todas as lojas'
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center justify-between w-full">
                    Todas as lojas
                  </span>
                </SelectItem>
                {availableStores.map(store => {
                  const storeCount = getStoreCount(store._id);
                  return (
                    <SelectItem key={store._id} value={store._id}>
                      <span className="truncate flex-1">{store.company}</span>
                      <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-4 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground flex-shrink-0">
                        {storeCount}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!hideSearch && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Digite aqui sua pesquisa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-muted-foreground">Filtros ativos</p>
            {hasActiveFilters && !hideClearAll && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium hover:underline"
                style={{ color: themeColors.primary }}
              >
                Limpar todos
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <Badge
                key={chip.key}
                variant="secondary"
                className="pl-2.5 pr-1 py-1 gap-1 font-normal text-xs rounded-full border max-w-full"
                style={{
                  borderColor: `${themeColors.primary}35`,
                  backgroundColor: `${themeColors.primary}12`,
                  color: themeColors.primary,
                }}
              >
                <span className="truncate max-w-[180px]">{chip.label}</span>
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="rounded-full p-0.5 shrink-0 hover:bg-black/10 transition-colors cursor-pointer"
                  aria-label={`Remover filtro ${chip.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {!useExternalScroll && <Separator />}

      {/* Scrollable Content */}
      <div
        className={
          useExternalScroll
            ? 'space-y-0'
            : isDrawerLayout
              ? 'wl6-inv-filters-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-0 pr-1 -mr-1 pb-4'
              : 'flex-1 overflow-y-auto -mr-5 pr-2 space-y-0'
        }
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
        }}
      >

        {/* Category Filter (Carro/Moto/Caminhão) - First Filter */}
        {categories && categories.length > 1 && (
          <FilterSection title="" section="category" alwaysExpanded>
            <div className="space-y-2">
              {categories.map((category: any) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value === selectedCategory ? 'all' : category.value)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${
                    selectedCategory === category.value ? '' : 'border-border hover:border-primary/30 hover:bg-muted/30'
                  }`}
                  style={selectedStyle(selectedCategory === category.value)}
                >
                  <span className={`text-sm ${selectedCategory === category.value ? 'font-medium' : ''}`}
                    style={selectedCategory === category.value ? { color: themeColors.primary } : {}}>
                    {category.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                      {getCategoryCount(category.value)}
                    </span>
                    {selectedCategory === category.value && (
                      <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </FilterSection>
        )}

        {/* Type Filter (Novo/Usado) - Style */}
        <FilterSection title="Tipo" section="type" alwaysExpanded>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSelectedType(selectedType === 'zerokm' ? 'all' : 'zerokm')}
              className="flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all"
              style={selectedStyle(selectedType === 'zerokm')}
            >
              <span
                className="text-sm font-medium"
                style={selectedType === 'zerokm' ? { color: themeColors.primary } : undefined}
              >
                Novos
              </span>
            </button>

            <button
              onClick={() => setSelectedType(selectedType === 'seminovo' ? 'all' : 'seminovo')}
              className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-border transition-all hover:border-primary/30"
              style={selectedStyle(selectedType === 'seminovo')}
            >
              <span
                className="text-sm font-medium"
                style={selectedType === 'seminovo' ? { color: themeColors.primary } : undefined}
              >
                Usados
              </span>
            </button>
          </div>
          {selectedType !== 'all' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedType('all')}
              className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Ver todos
            </Button>
          )}
        </FilterSection>


        {/* Brand Filter - Style with Logos */}
        <FilterSection title="Marca" section="brand" alwaysExpanded>
          {brandEntries.length > 0 && (
            <div className="space-y-3">
              {/* Popular Brands Grid with Logos */}
              <div className="grid grid-cols-3 gap-2">
                {(showAllBrands ? brandEntries : brandEntries.slice(0, 9)).map((entry) => {
                  const logo = getBrandLogo(entry.brand, entry.vehicleType);
                  const isSelected = isBrandEntrySelected(entry);
                  return (
                    <button
                      key={entry.key}
                      onClick={() => toggleBrandEntry(entry)}
                      className={`relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                        isSelected ? 'shadow-sm' : 'border-border hover:border-primary/30'
                      }`}
                      style={selectedStyle(isSelected)}
                    >
                      {logo ? (
                        <div className="w-full h-10 flex items-center justify-center mb-1">
                          <img
                            src={logo}
                            alt={entry.brand}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              // Fallback to text if image fails to load
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <span className="hidden text-xs font-semibold text-center">{entry.brand}</span>
                        </div>
                      ) : (
                        <div className="w-full h-10 flex items-center justify-center mb-1">
                          <span className="text-xs font-semibold text-center">{entry.brand}</span>
                        </div>
                      )}
                      <span
                        className="text-[10px] font-medium text-center line-clamp-1"
                        style={isSelected ? { color: themeColors.primary } : undefined}
                      >
                        {entry.brand}
                      </span>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: themeColors.primary }}>
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* "Ver todas as marcas" Button */}
              {brandEntries.length > 9 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllBrands(!showAllBrands)}
                  className="w-full text-xs h-9"
                >
                  {showAllBrands ? 'Ver menos marcas' : 'Ver todas as marcas'}
                </Button>
              )}
            </div>
          )}
        </FilterSection>

        {/* Model Filter */}
        {selectedBrand !== 'all' && models.length > 0 && (
          <FilterSection title="Modelo" section="model">
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger className="h-10 bg-background">
                <SelectValue placeholder="Selecione um modelo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os modelos</SelectItem>
                {models.map(model => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterSection>
        )}

        {/* Year Filter - Style */}
        <FilterSection title="Ano" section="year">
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-3">Escolha um intervalo</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Ano mínimo</label>
                  <Select
                    value={yearRange[0].toString()}
                    onValueChange={(value) => {
                      const newMin = parseInt(value);
                      // Adjust max if it's less than new min
                      if (yearRange[1] < newMin) {
                        setYearRange([newMin, newMin]);
                      } else {
                        setYearRange([newMin, yearRange[1]]);
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm bg-background">
                      <SelectValue placeholder={ranges.minYear.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      {generateMinYearOptions().map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Ano máximo</label>
                  <Select
                    value={yearRange[1].toString()}
                    onValueChange={(value) => {
                      const newMax = parseInt(value);
                      // Adjust min if it's greater than new max
                      if (yearRange[0] > newMax) {
                        setYearRange([newMax, newMax]);
                      } else {
                        setYearRange([yearRange[0], newMax]);
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm bg-background">
                      <SelectValue placeholder={ranges.maxYear.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      {generateMaxYearOptions()
                        .filter(year => year >= yearRange[0])
                        .map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Price Filter - Style */}
        <FilterSection title="Preço" section="price">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Escolha um intervalo</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Preço mínimo</label>
                <Select
                  value={priceRange[0].toString()}
                  onValueChange={(value) => {
                    const newMin = parseInt(value);
                    // Adjust max if it's less than new min
                    if (priceRange[1] < newMin) {
                      setPriceRange([newMin, newMin]);
                    } else {
                      setPriceRange([newMin, priceRange[1]]);
                    }
                  }}
                >
                  <SelectTrigger className="h-9 text-sm bg-background">
                    <SelectValue placeholder={formatPrice(ranges.minPrice)} />
                  </SelectTrigger>
                  <SelectContent>
                    {generatePriceOptions().map(price => (
                      <SelectItem key={price} value={price.toString()}>{formatPrice(price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Preço máximo</label>
                <Select
                  value={priceRange[1].toString()}
                  onValueChange={(value) => {
                    const newMax = parseInt(value);
                    // Adjust min if it's greater than new max
                    if (priceRange[0] > newMax) {
                      setPriceRange([newMax, newMax]);
                    } else {
                      setPriceRange([priceRange[0], newMax]);
                    }
                  }}
                >
                  <SelectTrigger className="h-9 text-sm bg-background">
                    <SelectValue placeholder={formatPrice(ranges.maxPrice)} />
                  </SelectTrigger>
                  <SelectContent>
                    {generatePriceOptions()
                      .filter(price => price >= priceRange[0])
                      .map(price => (
                        <SelectItem key={price} value={price.toString()}>{formatPrice(price)}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Mileage Filter - Style */}
        <FilterSection title="Quilometragem" section="mileage">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Km mínimo</label>
                <Select
                  value={mileageRange[0].toString()}
                  onValueChange={(value) => {
                    const newMin = parseInt(value);
                    // Adjust max if it's less than new min
                    if (mileageRange[1] < newMin) {
                      setMileageRange([newMin, newMin]);
                    } else {
                      setMileageRange([newMin, mileageRange[1]]);
                    }
                  }}
                >
                  <SelectTrigger className="h-9 text-sm bg-background">
                    <SelectValue placeholder={`${ranges.minMileage.toLocaleString('pt-BR')} km`} />
                  </SelectTrigger>
                  <SelectContent>
                    {generateMileageOptions().map(km => (
                      <SelectItem key={km} value={km.toString()}>
                        {km === 0 ? '0 km' : km >= 1000 ? `${(km / 1000).toFixed(0)} mil km` : `${km} km`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Km máximo</label>
                <Select
                  value={mileageRange[1].toString()}
                  onValueChange={(value) => {
                    const newMax = parseInt(value);
                    // Adjust min if it's greater than new max
                    if (mileageRange[0] > newMax) {
                      setMileageRange([newMax, newMax]);
                    } else {
                      setMileageRange([mileageRange[0], newMax]);
                    }
                  }}
                >
                  <SelectTrigger className="h-9 text-sm bg-background">
                    <SelectValue placeholder={`${(ranges.maxMileage / 1000).toFixed(0)} mil km`} />
                  </SelectTrigger>
                  <SelectContent>
                    {generateMileageOptions()
                      .filter(km => km >= mileageRange[0])
                      .map(km => (
                        <SelectItem key={km} value={km.toString()}>
                          {km === 0 ? '0 km' : km >= 1000 ? `${(km / 1000).toFixed(0)} mil km` : `${km} km`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Fuel Filter */}
        <FilterSection title="Combustível" section="fuel">
          <div className="space-y-2">
            {fuels.length > 0 && fuels.map(fuel => {
              const isSelected = selectedFuels.includes(fuel);
              return (
                <button
                  key={fuel}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedFuels(selectedFuels.filter(f => f !== fuel));
                    } else {
                      setSelectedFuels([...selectedFuels, fuel]);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                >
                  <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}
                    style={isSelected ? { color: themeColors.primary } : {}}>
                    {fuel}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                      {getFuelCount(fuel)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Transmission Filter */}
        <FilterSection title="Câmbio" section="transmission">
          <div className="space-y-2">
            {transmissions.length > 0 && transmissions.map(transmission => {
              const isSelected = selectedTransmissions.includes(transmission);
              return (
                <button
                  key={transmission}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedTransmissions(selectedTransmissions.filter(t => t !== transmission));
                    } else {
                      setSelectedTransmissions([...selectedTransmissions, transmission]);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                >
                  <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}
                    style={isSelected ? { color: themeColors.primary } : {}}>
                    {transmission}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                      {getTransmissionCount(transmission)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Color Filter */}
        <FilterSection title="Cor" section="color">
          <div className="space-y-2">
            {colors.length > 0 && colors.map(color => {
              const isSelected = selectedColors.includes(color);
              return (
                <button
                  key={color}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedColors(selectedColors.filter(c => c !== color));
                    } else {
                      setSelectedColors([...selectedColors, color]);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                >
                  <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}
                    style={isSelected ? { color: themeColors.primary } : {}}>
                    {color}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                      {getColorCount(color)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Armored Filter */}
        {setIsArmoredFilter && (
        <FilterSection title="Blindados" section="armored">
          <div className="space-y-2">
            <button
              onClick={() => setIsArmoredFilter(!isArmoredFilter)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${
                isArmoredFilter
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/30 hover:bg-muted/30'
              }`}
            >
              <span className={`text-sm ${isArmoredFilter ? 'font-medium' : ''}`}
                style={isArmoredFilter ? { color: themeColors.primary } : {}}>
                Apenas Blindados
              </span>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                  {vehicles.filter(v => v.armored).length}
                </span>
                {isArmoredFilter && (
                  <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                )}
              </div>
            </button>
          </div>
        </FilterSection>
        )}

        {/* Optionals Filter */}
        <FilterSection title="Opcionais" section="optionals">
          <div className="space-y-2">
            {optionals.length > 0 && optionals.map(optional => {
              const isSelected = selectedOptionals.includes(optional);
              return (
                <button
                  key={optional}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedOptionals(selectedOptionals.filter(o => o !== optional));
                    } else {
                      setSelectedOptionals([...selectedOptionals, optional]);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all text-left ${isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                >
                  <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}
                    style={isSelected ? { color: themeColors.primary } : {}}>
                    {optional}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                      {getOptionalCount(optional)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4" style={{ color: themeColors.primary }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </FilterSection>
      </div>
      {/* End of Scrollable Content */}
    </div>
  );
};

